/**
 * Predictive search in the header search modal: debounced fetch of sections/predictive-search.liquid
 * through /search/suggest (Section Rendering API), arrow-key navigation, live result count.
 */
if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[type="search"]');
        this.results = this.querySelector('[data-results]');
        this.status = this.querySelector('[data-status]');
        this.cache = new Map();
        let timer;
        this.input.addEventListener('input', () => {
          clearTimeout(timer);
          timer = setTimeout(() => this.search(this.input.value.trim()), 250);
        });
        this.addEventListener('keydown', (event) => this.onKeydown(event));
        if (this.input.value.trim()) this.search(this.input.value.trim());
      }

      async search(terms) {
        if (terms.length < 2) {
          this.render('', 0);
          return;
        }
        if (this.cache.has(terms)) {
          this.render(...this.cache.get(terms));
          return;
        }
        this.controller?.abort();
        this.controller = new AbortController();
        this.results.setAttribute('aria-busy', 'true');
        try {
          const response = await this.fetchResults(terms);
          if (!response.ok) throw new Error(response.status);
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const markup = doc.querySelector('.predictive-search__results');
          const entry = [markup ? markup.outerHTML : '', Number(markup?.dataset.count || 0)];
          this.cache.set(terms, entry);
          if (this.input.value.trim() === terms) this.render(...entry);
        } catch (error) {
          if (error.name !== 'AbortError') this.render('', 0);
        } finally {
          this.results.removeAttribute('aria-busy');
        }
      }

      /** Predictive search first; languages it doesn't support (417, e.g. Hebrew) use the regular search. */
      async fetchResults(terms) {
        const signal = this.controller.signal;
        if (!this.useSearch) {
          const params = new URLSearchParams({
            q: terms,
            section_id: 'predictive-search',
            'resources[type]': 'product,collection,page',
            'resources[limit]': '6',
            'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
          });
          const response = await fetch(`${this.dataset.url}?${params}`, { signal });
          if (response.status !== 417) return response;
          this.useSearch = true;
        }
        const params = new URLSearchParams({
          q: terms,
          'options[prefix]': 'last',
          type: 'product,page',
          section_id: 'predictive-search',
        });
        return fetch(`${this.dataset.searchUrl}?${params}`, { signal });
      }

      render(markup, count) {
        this.results.innerHTML = markup;
        this.results.hidden = !markup;
        this.status.textContent = markup ? this.dataset.resultsTemplate.replace('__COUNT__', count) : '';
      }

      /** Arrow keys move between the input and the result links. */
      onKeydown(event) {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        const items = [...this.results.querySelectorAll('[data-result]')];
        if (!items.length || this.results.hidden) return;
        const index = items.indexOf(document.activeElement);
        event.preventDefault();
        if (event.key === 'ArrowDown') {
          items[Math.min(index + 1, items.length - 1)].focus();
        } else if (index <= 0) {
          this.input.focus();
        } else {
          items[index - 1].focus();
        }
      }
    }
  );
}
